/* Manager.tsx — coverage, for the person who has to DEFEND the dataset rather than sell from it.
 *
 * WHAT IS DELIBERATELY ABSENT: territory and quota roll-ups. Nothing is assigned until GMR sends a
 * real book of business, and assignment by rule over plant geography was removed
 * (0 reps assigned). Empty charts render as if they were data, which is worse than an absent
 * screen — it invites a conclusion from a blank.
 *
 * The coverage question that DOES matter today is whether an account can be seen at all: the Atlas
 * plots geocoded plants only, so an account whose plants carry no address is absent from the map,
 * from every state filter and from every regional count, no matter how many contacts we hold.
 */
import { useEffect, useState } from 'react';
import { api, isErr } from '../lib/api.ts';
import { N, SQ } from '../lib/fmt.ts';
import { Chip, ErrBox, Hint, RTable, Section, ShareCell, TD, TR } from '../components/ui.tsx';
import { Matrix, type Cell } from '../components/data.tsx';

export interface Coverage {
  byTier: { tier: string; accounts: number; plants: number; buyers: number; reach: number; dark: number; no_dm: number }[];
  byMap: { state: string; accounts: number; plants: number; sqft: number; buyers: number; reach: number; t1: number; dark: number }[];
  offMapTop: { name: string; domain: string; facilities: number; geo_facilities: number; reach: number }[];
  gaps: { k: string; v: number; why: string; ent: string; q: unknown }[];
  topDark: { name: string; domain: string; tier: string; facilities: number; sqft: number; people: number; buyers: number }[];
  deptLevel: Cell[];
}

export function useCoverage() {
  const [c, setC] = useState<Coverage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let dead = false;
    api<Coverage>('coverage').then((r) => {
      if (dead) return;
      if (isErr(r)) setErr(r.error); else setC(r);
    });
    return () => { dead = true; };
  }, []);
  return { c, err };
}

export function Manager({ onOpenGap }: { onOpenGap?: (ent: string, q: unknown) => void }) {
  const { c, err } = useCoverage();
  if (err) return <ErrBox what="The manager view did not load" msg={err} />;
  if (!c) return <Hint style={{ padding: 'var(--gut)' }}>loading coverage…</Hint>;

  const totalAccts = c.byMap.reduce((a, r) => a + (r.accounts || 0), 0);
  const offMap = c.byMap.find((r) => r.state !== 'on the map');

  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>
      <Section label="Who is on the map, and who is missing from it">
        <Hint style={{ maxWidth: 900 }}>
          Nothing is assigned to anyone yet, so there are no books to compare. The coverage question that
          matters today is whether an account can be SEEN at all: the Atlas plots geocoded plants only, so
          an account whose plants carry no address is absent from the map, from every state filter and from
          regional counts — no matter how many contacts we hold for it.
        </Hint>
        <div style={{ marginTop: 10, overflowX: 'auto' }}>
          <RTable cols={[{ k: 'Status' }, { k: 'Accounts', num: true }, { k: 'T1', num: true }, { k: 'Plants', num: true },
            { k: 'Sq ft', num: true }, { k: 'In a buying role', num: true }, { k: 'Reachable', num: true },
            { k: 'Nobody reachable', num: true }, { k: 'Share of accounts', w: 190 }]}>
            {c.byMap.map((r) => {
              const off = r.state !== 'on the map';
              return (
                <TR key={r.state}>
                  <TD><b style={{ color: off ? 'var(--rd)' : 'var(--ok)' }}>{r.state}</b></TD>
                  <TD num>{N(r.accounts)}</TD>
                  <TD num>{N(r.t1)}</TD>
                  <TD num>{N(r.plants)}</TD>
                  <TD num>{SQ(r.sqft)}</TD>
                  <TD num>{N(r.buyers)}</TD>
                  <TD num color="var(--ok)">{N(r.reach)}</TD>
                  <TD num color="var(--dim)">{N(r.dark)}</TD>
                  <ShareCell n={r.accounts} total={totalAccts} />
                </TR>
              );
            })}
          </RTable>
        </div>
        {offMap && (
          <Hint style={{ marginTop: 8, maxWidth: 900 }}>
            <b style={{ color: 'var(--rd)' }}>{N(offMap.accounts)} accounts carrying {N(offMap.reach)} reachable contacts have no dot on the Atlas.</b>{' '}
            Their plants are on file but hold no usable address — the scrape captured facility names, or a corporate
            worldwide-locations page, instead of the North American sites. That is a scrape gap, not a coverage decision.
          </Hint>
        )}
        <Hint style={{ marginTop: 6, maxWidth: 900 }}>
          Territory and quota roll-ups belong here and are deliberately absent: nothing is assigned until GMR
          sends a real book of business. Assignment by rule over plant geography was removed.
        </Hint>
      </Section>

      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 520px', minWidth: 0, borderRight: 'var(--hair)' }}>
          <Section label="By tier — where the market actually is">
            <Hint>The bar is each tier's share of the {N(totalAccts)} accounts, on the same scale as the table above.</Hint>
            <div style={{ marginTop: 8, overflowX: 'auto' }}>
              <RTable cols={[{ k: 'Tier' }, { k: 'Accounts', num: true }, { k: 'Share', w: 150 }, { k: 'Plants', num: true },
                { k: 'In a buying role', num: true }, { k: 'Reachable', num: true }, { k: 'Nobody reachable', num: true }, { k: 'No decision maker', num: true }]}>
                {c.byTier.map((t) => (
                  <TR key={t.tier}>
                    <TD><Chip tone={t.tier === '1' ? 'warn' : 'none'}>T{t.tier}</Chip></TD>
                    <TD num>{N(t.accounts)}</TD>
                    <ShareCell n={t.accounts} total={totalAccts} />
                    <TD num>{N(t.plants)}</TD>
                    <TD num>{N(t.buyers)}</TD>
                    <TD num color="var(--ok)">{N(t.reach)}</TD>
                    <TD num color="var(--rd)">{N(t.dark)}</TD>
                    <TD num>{N(t.no_dm)}</TD>
                  </TR>
                ))}
              </RTable>
            </div>
          </Section>
        </div>
        <div style={{ flex: '1 1 420px', minWidth: 0 }}>
          <Section label="Tier 1 off the map — the expensive end of the gap">
            <div style={{ overflowX: 'auto' }}>
              <RTable cols={[{ k: 'Account' }, { k: 'Plants on file', num: true }, { k: 'Geocoded', num: true }, { k: 'Reachable', num: true }]}>
                {c.offMapTop.map((a) => (
                  <TR key={a.domain}>
                    <TD><b>{a.name}</b></TD>
                    <TD num>{N(a.facilities)}</TD>
                    <TD num color="var(--rd)">{N(a.geo_facilities)}</TD>
                    <TD num color="var(--ok)">{N(a.reach)}</TD>
                  </TR>
                ))}
              </RTable>
            </div>
            <Hint style={{ marginTop: 8 }}>
              Every one of these has plants on file and none of them geocoded. They are not foreign companies and
              they are not out of scope — we simply never captured a usable North American address for them.
            </Hint>
          </Section>
        </div>
      </div>

      <Section label="Biggest accounts with a buying group and nothing to reach it with">
        <Hint>Not a data failure — a decision. Each of these is a priced enrichment target, and the manager is the one who should see them.</Hint>
        <div style={{ marginTop: 8, overflowX: 'auto' }}>
          <RTable cols={[{ k: 'Account' }, { k: 'Domain' }, { k: 'Tier', num: true }, { k: 'Plants', num: true },
            { k: 'Sq ft', num: true }, { k: 'Contacts we hold', num: true }, { k: 'In a buying role', num: true }]}>
            {c.topDark.map((a) => (
              <TR key={a.domain}>
                <TD><b>{a.name}</b></TD>
                <TD style={{ fontFamily: 'var(--mono)', color: 'var(--dim2)' }}>{a.domain}</TD>
                <TD num>T{a.tier}</TD>
                <TD num>{N(a.facilities)}</TD>
                <TD num>{SQ(a.sqft)}</TD>
                <TD num>{N(a.people)}</TD>
                <TD num>{N(a.buyers)}</TD>
              </TR>
            ))}
          </RTable>
        </div>
      </Section>

      {/* Every gap carries the filter that reproduces it, so the count on this page is a LINK into
          the grid showing exactly those rows. A number you cannot open is a number nobody trusts. */}
      <Section label="Every gap, with its cause and where it opens">
        <Hint>A number without its method is a rumour. Each row here is a live count, not a note from a previous session.</Hint>
        <div style={{ marginTop: 10, overflowX: 'auto' }}>
          <RTable cols={[{ k: 'Gap' }, { k: 'Rows', num: true }, { k: 'Cause / next move' }, { k: '', w: 150 }]}>
            {c.gaps.map((g) => (
              <TR key={g.k}>
                <TD style={{ whiteSpace: 'normal' }}>{g.k}</TD>
                <TD num color="var(--am)">{N(g.v)}</TD>
                <TD color="var(--dim)" style={{ whiteSpace: 'normal' }}>{g.why}</TD>
                <TD>
                  {g.q != null && onOpenGap && (
                    <button onClick={() => onOpenGap(g.ent, g.q)}
                      style={{ border: 'var(--hair)', background: 'transparent', color: 'var(--dim)', cursor: 'pointer', padding: '3px 7px', fontSize: 'var(--t-micro)', fontFamily: 'var(--sans)' }}>
                      Open the rows →
                    </button>
                  )}
                </TD>
              </TR>
            ))}
          </RTable>
        </div>
      </Section>

      <Section label="Where the buying power sits — department × level, whole market">
        {/* No handler on purpose: nothing to open from here yet, so the matrix suppresses the click
            affordance and the "click a cell" hint rather than inviting a dead control. */}
        <Matrix cells={c.deptLevel} />
      </Section>
    </div>
  );
}
